import { CoreButton } from '@ingenico/react-ui/core-button';
import { getHumanizedFileSize, type FileWithStatus } from '@ingenico/react-ui/file-handlerV2';
import type { ReactElement } from 'react';
import styles from './FileUploadItemPreview.module.scss';
import { Tooltip } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import classNames from 'classnames';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useTranslation } from 'react-i18next';
import { mapMimeTypeToFileTypeIcon } from './fileTypeIcon.util';
import { FileTypeIcon } from './FileTypeIcon.ctrl';
import { ReactComponent as ErrorIcon } from '../../assets/error.svg';

export interface FileUploadItemPreviewProps {
  readonly file: FileWithStatus;
  readonly previewSizeBase?: 2 | 10;
  readonly onRemove?: () => void;
  readonly hasError?: boolean;
  readonly isReadonly?: boolean;
  readonly componentsClassNames?: Partial<Record<'container', string>>;
}

export function FileUploadItemPreview({
  file,
  previewSizeBase = 10,
  onRemove,
  hasError,
  isReadonly,
  componentsClassNames,
}: FileUploadItemPreviewProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  return (
    <div className={classNames(styles.container, componentsClassNames?.container, { [styles.hasError]: hasError })}>
      <div className={styles.fileInfo__container}>
        <span className={styles.fileInfo__icon}>
          {hasError ? <ErrorIcon /> : <FileTypeIcon name={mapMimeTypeToFileTypeIcon(file.file?.type ?? '')} />}
        </span>
        <div className={styles.fileInfo__labelsContainer}>
          <CoreTypography variant={CoreTypographyVariant.Body03Regular}>{file.name}</CoreTypography>
          <CoreTypography variant={CoreTypographyVariant.Body03Medium}>
            {[file.size ? getHumanizedFileSize(file.size, t, previewSizeBase) : undefined].join(' • ')}
          </CoreTypography>
        </div>
      </div>
      {!isReadonly && (
        <Tooltip title={t('button.delete')}>
          <CoreButton onClick={onRemove} className={styles.removeButton} variant="text">
            <DeleteOutlinedIcon />
          </CoreButton>
        </Tooltip>
      )}
    </div>
  );
}
